import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, relTime, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";

/** Moderation queue (FR-SYS-08): reported chat messages, feed posts, prayer requests and check-ins. */

interface ReportRow {
  id: string;
  targetType: "message" | "feed_post" | "prayer_request" | "check_in";
  targetId: string;
  reason: string | null;
  status: "open" | "hidden" | "dismissed" | "removed";
  createdAt: string;
  decidedAt: string | null;
  reporterAlias: string;
  content: string | null;
  authorAlias: string | null;
  priorReports: number;
}

export function AdminModeration() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [status, setStatus] = useState<"open" | "decided">("open");
  const q = useQuery({ queryKey: ["admin", "reports", scope.churchId, status], queryFn: () => api<{ reports: ReportRow[] }>(`/admin/reports${scopeQuery(scope, "church", { status })}`) });
  const decide = useMutation({
    mutationFn: (p: { id: string; action: "hide" | "remove" | "dismiss" }) => api(`/admin/reports/${p.id}/decide`, { method: "POST", json: { action: p.action } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "reports"] });
      qc.invalidateQueries({ queryKey: ["admin", "attention"] });
    },
  });

  return (
    <>
      <h1>{t("admin.moderation.title")}</h1>
      <p className="muted">{t("admin.moderation.body")}</p>
      <div className="tabs">
        {(["open", "decided"] as const).map((k) => (
          <button key={k} className={k === status ? "active" : ""} onClick={() => setStatus(k)}>
            {t(`admin.moderation.${k}`)}
          </button>
        ))}
      </div>
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      {q.data?.reports.length === 0 ? <p className="muted empty">{t("admin.moderation.empty")}</p> : null}
      <div className="list">
        {q.data?.reports.map((r) => (
          <div key={r.id} className="card report">
            <div className="report-head">
              <span className="pill">{t(`admin.moderation.type.${r.targetType}`)}</span>
              <span className={`pill ${r.status === "open" ? "warn" : ""}`}>{t(`admin.moderation.status.${r.status}`)}</span>
              {r.priorReports > 0 ? <span className="pill critical">{t("admin.moderation.prior", { count: r.priorReports })}</span> : null}
              <span className="muted small">{relTime(r.createdAt, locale)}</span>
            </div>
            <blockquote>{r.content ?? "—"}</blockquote>
            <div className="muted small">
              {r.authorAlias ? t("admin.moderation.author", { name: r.authorAlias }) : null} · {t("admin.moderation.reportedBy", { name: r.reporterAlias })}
              {r.reason ? ` · „${r.reason}“` : ""}
            </div>
            {r.status === "open" ? (
              <div className="actions">
                <button className="btn small" title={t("admin.moderation.hideHint")} disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, action: "hide" })}>
                  {t("admin.moderation.hide")}
                </button>
                <button className="btn small danger" title={t("admin.moderation.removeHint")} disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, action: "remove" })}>
                  {t("admin.moderation.remove")}
                </button>
                <button className="btn small ghost" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, action: "dismiss" })}>
                  {t("admin.moderation.dismiss")}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ prayer wall overview */

interface WallRow {
  id: string;
  authorAlias: string | null;
  anonymous: boolean;
  urgent: boolean;
  category: string | null;
  body: string;
  visibility: "fam" | "church" | "all";
  escalatedAt: string | null;
  answeredAt: string | null;
  withdrawnAt: string | null;
  crisisFlaggedAt: string | null;
  createdAt: string;
  prayerCount: number;
}

export function AdminPrayerWall() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "prayer", scope.churchId], queryFn: () => api<{ requests: WallRow[] }>(`/admin/prayer${scopeQuery(scope, "church")}`) });
  const withdraw = useMutation({
    mutationFn: (id: string) => api(`/admin/prayer/${id}/withdraw`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prayer"] });
      qc.invalidateQueries({ queryKey: ["admin", "attention"] });
    },
  });
  return (
    <>
      <h1>{t("admin.prayerWall.title")}</h1>
      <p className="muted">{t("admin.prayerWall.body")}</p>
      {q.data?.requests.length === 0 ? <p className="muted empty">{t("admin.prayerWall.empty")}</p> : null}
      <div className="list">
        {q.data?.requests.map((r) => (
          <div key={r.id} className={`card report ${r.withdrawnAt ? "dim" : ""}`}>
            <div className="report-head">
              {r.urgent ? <span className="pill critical">{t("prayer.urgent")}</span> : null}
              {r.crisisFlaggedAt ? <span className="pill critical">{t("admin.prayerWall.crisis")}</span> : null}
              {r.visibility === "fam" ? <span className="pill warn">{t("admin.prayerWall.famWindow")}</span> : null}
              {r.answeredAt ? <span className="pill live">{t("admin.prayerWall.answered")}</span> : null}
              {r.withdrawnAt ? <span className="pill">{t("admin.prayerWall.withdrawn")}</span> : null}
              {r.category ? <span className="pill">{t(`prayer.cat.${r.category}`)}</span> : null}
              <span className="muted small">{relTime(r.createdAt, locale)}</span>
            </div>
            <blockquote>{r.body}</blockquote>
            <div className="muted small">
              {r.anonymous ? t("prayer.anonymousLabel") : r.authorAlias} · {t("admin.prayerWall.prayers", { count: r.prayerCount })}
            </div>
            {!r.withdrawnAt ? (
              <div className="actions">
                <button className="btn small ghost" disabled={withdraw.isPending} onClick={() => withdraw.mutate(r.id)}>
                  {t("admin.prayerWall.withdraw")}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
}
